import React, {
  useEffect,
  useState,
  useRef,
  CSSProperties,
  useMemo,
  RefObject,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import DropdownItemAtom from "../item";
import styles from "../container/DropdownContainerAtom.module.css";
import { useDevice } from "../../../hooks/useDevice";
import ActionSheet, { ActionSheetType } from "../../../components/action-sheet";
import { DropdownContainerType, DropdownItemType } from "../container/";

const dropdownVariants = {
  hidden: {
    opacity: 0,
    maxHeight: 0,
    transition: { duration: 0.2 },
    display: "none",
  },
  visible: {
    opacity: 1,
    maxHeight: "500px",
    transition: { duration: 0.2 },
    display: "block",
  },
};

/**
 * Scrolls the given list container to the item at the specified index.
 * @param listContainer The container element to scroll.
 * @param itemIdx The index of the item to scroll to (not counting padding items).
 * @param itemSize "sm" or "md" (determines item height).
 */
export function scrollListContainerToItem(
  listContainer: HTMLDivElement,
  itemIdx: number,
  itemSize: "sm" | "md" = "sm"
) {
  const itemHeight = itemSize === "sm" ? 32 : 48;
  listContainer.scrollTo({
    top: itemHeight * itemIdx,
    behavior: "smooth",
  });
}

export interface WheelpickerContainerType extends DropdownContainerType {
  itemWidth?: "fill" | "fit-content";
  actionsheet?: ActionSheetType;
}

export const WheelpickerContainerAtom: React.FC<WheelpickerContainerType> = ({
  className = "",
  width = "200px",
  parent,
  isOpen = false,
  data = [],
  itemSize = "sm",
  itemWidth = "fill",
  selected: selectedItems = [],
  offsetTop = 0,
  textAlignment = "left",
  onChange,
  actionsheet,
  customContent,
}) => {
  const { isDesktop } = useDevice();
  const containerRef = useRef<HTMLDivElement>(null);
  const [topPosition, setTopPosition] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [selected, setSelected] = useState<
    DropdownItemType | DropdownItemType[]
  >(selectedItems);
  const wheelpickerRef = useRef<HTMLDivElement>(null);
  const activeContainerRef = useRef<HTMLDivElement | null>(null);
  const lastSelectedRef = useRef<DropdownItemType[]>([]);
  const centerCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  let height: string | undefined;

  // Wheelpicker sizing
  if (itemSize === "sm") {
    height = "177px";
  } else if (itemSize === "md") {
    height = "258px";
  }

  const dropdownContainerStyle: CSSProperties = useMemo(() => {
    return {
      overflow: "hidden",
      maxHeight: height,
      width: width,
      top: `${topPosition}px`,
    };
  }, [height, topPosition]);

  useEffect(() => {
    if (parent.current && isOpen) {
      const elementHeight =
        parent.current.getBoundingClientRect().height + offsetTop;
      setTopPosition(elementHeight);
    }
  }, [parent, isOpen]);

  // Scroll to the selected item on open
  useEffect(() => {
    if (selected && Array.isArray(selected) && isOpen && !isMounted) {
      setTimeout(() => {
        setIsMounted(true);
      }, 300);

      setTimeout(() => {
        selected.forEach((item, index) => {
          const itemIdx = data[index]?.items?.findIndex(
            (i) => i.value === item.value
          );
          if (itemIdx !== undefined && itemIdx >= 0) {
            const listContainer = document.querySelector(
              `.wheelpicker-container-list[data-list-index="${index}"]`
            ) as HTMLDivElement;
            if (listContainer) {
              scrollListContainerToItem(listContainer, itemIdx, itemSize);
            }
          }
        });
      }, 10);
    }
  }, [selected, isOpen]);

  // Add this function above the useEffect for keyboard controls
  const handleListNavigation = (eventOrKey: KeyboardEvent | string) => {
    if (!isOpen || !activeContainerRef.current) return;

    const container = activeContainerRef.current;
    const itemHeight = itemSize === "sm" ? 32 : 48;

    let key: string;
    let preventDefault = () => {};

    if (typeof eventOrKey === "string") {
      key = eventOrKey;
    } else {
      key = eventOrKey.key;
      preventDefault = () => eventOrKey.preventDefault();
    }

    switch (key) {
      case "ArrowUp":
        preventDefault();
        container.scrollBy({ top: -itemHeight, behavior: "smooth" });
        break;
      case "ArrowDown":
        preventDefault();
        container.scrollBy({ top: itemHeight, behavior: "smooth" });
        break;
      case "PageUp":
        preventDefault();
        container.scrollBy({ top: -itemHeight * 5, behavior: "smooth" });
        break;
      case "PageDown":
        preventDefault();
        container.scrollBy({ top: itemHeight * 5, behavior: "smooth" });
        break;
      case "ArrowLeft":
        preventDefault();
        moveActiveContainer("left");
        break;
      case "ArrowRight":
        preventDefault();
        moveActiveContainer("right");
        break;
      default:
        break;
    }
  };

  // Update the useEffect for keyboard controls:
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      handleListNavigation(e);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, activeContainerRef]);

  const defaultRenderItem = (
    {
      label,
      value,
      iconLeft,
      iconRight,
      className,
      itemKey,
      state = "default",
    }: DropdownItemType,
    listContainer: string
  ) => {
    const isItemSelected =
      !Array.isArray(selected) && selected?.value === value;

    console.log({ textAlignment });
    return (
      <DropdownItemAtom
        className={className}
        size={itemSize}
        width={itemWidth}
        label={label}
        value={value}
        type={"text"}
        textAlignment={textAlignment}
        state={state}
        itemKey={itemKey}
        key={`item-${label}-${value}`}
        selected={isItemSelected}
        onClick={() => {
          const container = document.querySelector(listContainer);
          if (container instanceof HTMLDivElement && itemKey !== undefined) {
            scrollListContainerToItem(container, itemKey, itemSize);
          }
        }}
        iconLeft={iconLeft}
        iconRight={iconRight}
      />
    );
  };

  const startCenterCheckInterval = () => {
    centerCheckIntervalRef.current = setInterval(() => {
      // Loop over all .wheelpicker-container-list elements
      const containers = wheelpickerRef.current?.querySelectorAll<HTMLElement>(
        ".wheelpicker-container-list"
      );

      if (containers) {
        containers.forEach((container) => {
          // Only get items with the 'active-item' class
          const items = Array.from(container.getElementsByClassName("active-item")) as HTMLElement[];
          const containerRect = container.getBoundingClientRect();
          const containerCenter = containerRect.top + containerRect.height / 2;
          let closestItem: HTMLElement | null = null;
          let minDistance = Infinity;
          items.forEach((item: HTMLElement) => {
            const rect = item.getBoundingClientRect();
            const itemCenter = rect.top + rect.height / 2;
            const distance = Math.abs(itemCenter - containerCenter);
            if (distance < minDistance) {
              minDistance = distance;
              closestItem = item;
            }
          });

          if (closestItem) {
            items.forEach((sibling) => {
              const desiredState =
                sibling === closestItem ? "default" : "disabled";
              if (sibling.getAttribute("data-state") !== desiredState) {
                sibling.setAttribute("data-state", desiredState);
              }
            });
          }
        });

        // After the containers.forEach loop, collect newSelected
        const newSelected = Array.from(containers)
          .map((container) => {
            // Only get items with the 'active-item' class
            const items = Array.from(container.getElementsByClassName("active-item")) as HTMLElement[];
            const containerRect = container.getBoundingClientRect();
            const containerCenter = containerRect.top + containerRect.height / 2;
            let closestItem: HTMLElement | null = null;
            let minDistance = Infinity;
            items.forEach((item: HTMLElement) => {
              const rect = item.getBoundingClientRect();
              const itemCenter = rect.top + rect.height / 2;
              const distance = Math.abs(itemCenter - containerCenter);
              if (distance < minDistance) {
                minDistance = distance;
                closestItem = item;
              }
            });
            if (closestItem) {
              return {
                label:
                  (closestItem as HTMLElement).getAttribute(
                    "data-item-label"
                  ) || "",
                value:
                  (closestItem as HTMLElement).getAttribute(
                    "data-item-value"
                  ) || "",
              };
            }
            return undefined;
          })
          .filter((item): item is { label: string; value: string } =>
            Boolean(item)
          );

        // Set the newSelected state only if array is not empty
        const isEqual =
          lastSelectedRef.current.length === newSelected.length &&
          lastSelectedRef.current.every(
            (item, idx) =>
              item.label === newSelected[idx].label &&
              item.value === newSelected[idx].value
          );

        if (!isEqual && newSelected.length > 0) {
          setSelected(newSelected);
          lastSelectedRef.current = newSelected;

          // Only fire onChange if at least one label or value is not an empty string
          const hasNonEmpty = newSelected.some(
            (item) => item.label !== "" || item.value !== ""
          );

          if (onChange && hasNonEmpty) {
            onChange(newSelected);
          }
        }
      }
    }, 10);
    return () => {
      if (centerCheckIntervalRef.current) {
        clearInterval(centerCheckIntervalRef.current);
        centerCheckIntervalRef.current = null;
      }
    };
  };

  // Create content for action sheet
  const renderDropdownContent = () => {
    if (customContent) {
      return customContent;
    } else {
      return (
        <div
          className={`${styles.wheelpickerContainer} wheelpicker-container`}
          data-size={itemSize}
          ref={wheelpickerRef}
        >
          {data?.map(({ items, extra }, key) => (
            <div
              data-size={itemSize}
              data-list-index={key}
              className={`${styles.wheelpickerList} wheelpicker-container-list wheelpicker-container-list-${key}`}
              key={key}
              onMouseOver={(e) => {
                const target = e.currentTarget;
                if (target !== activeContainerRef.current) {
                  activeContainerRef.current = target;
                }
              }}
            >
              {extra && (
                <span className={`item-list-extra ${styles.itemListExtra}`}>
                  {extra}
                </span>
              )}

              <DropdownItemAtom
                className={styles.wheelpickerItem}
                size={itemSize}
                label=""
                noFocus={true}
                width={itemWidth}
              />

              <DropdownItemAtom
                size={itemSize}
                label=""
                className={styles.wheelpickerItem}
                noFocus={true}
                width={itemWidth}
              />
              {items.length > 0 &&
                items.map((item, itemKey) =>
                  defaultRenderItem(
                    {
                      ...item,
                      className: `${styles.wheelpickerItem} active-item`,
                      itemKey: itemKey,
                    },
                    `.wheelpicker-container-list-${key}`
                  )
                )}
              <DropdownItemAtom
                size={itemSize}
                label=""
                className={styles.wheelpickerItem}
                noFocus={true}
                width={itemWidth}
              />
              <DropdownItemAtom
                size={itemSize}
                label=""
                className={styles.wheelpickerItem}
                width={itemWidth}
              />
            </div>
          ))}
        </div>
      );
    }
  };

  useEffect(() => {
    if (isMounted) {
      const stopInterval = startCenterCheckInterval();

      return () => {
        stopInterval();
      };
    }
  }, [isMounted]);

  useEffect(() => {
    if (!isOpen) {
      if (centerCheckIntervalRef.current) {
        clearInterval(centerCheckIntervalRef.current);
        centerCheckIntervalRef.current = null;
        setIsMounted(false);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const containers = document.querySelectorAll<HTMLElement>(
      ".wheelpicker-container-list"
    );

    const handleNativeWheel = (e: WheelEvent) => {
      const container = e.currentTarget as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isScrollingDown = e.deltaY > 0;

      if (
        (isScrollingDown && scrollTop + clientHeight >= scrollHeight) ||
        (!isScrollingDown && scrollTop <= 0)
      ) {
        e.preventDefault();
      }
    };

    containers.forEach((container) => {
      container.addEventListener("wheel", handleNativeWheel, {
        passive: false,
      });
    });

    return () => {
      containers.forEach((container) => {
        container.removeEventListener("wheel", handleNativeWheel);
      });
    };
  }, [isMounted]);

  const moveActiveContainer = (direction: "left" | "right") => {
    if (!activeContainerRef.current) return;
    let sibling =
      direction === "left"
        ? activeContainerRef.current.previousElementSibling
        : activeContainerRef.current.nextElementSibling;
    while (
      sibling &&
      !sibling.classList.contains("wheelpicker-container-list")
    ) {
      sibling =
        direction === "left"
          ? sibling.previousElementSibling
          : sibling.nextElementSibling;
    }
    if (sibling && sibling.classList.contains("wheelpicker-container-list")) {
      activeContainerRef.current = sibling as HTMLDivElement;
      (sibling as HTMLDivElement).focus?.();
    }
  };

  return (
    isOpen &&
    (data.length > 0 || customContent) && (
      <>
        {isDesktop ? (
          <AnimatePresence>
            <motion.div
              ref={containerRef}
              className={[
                styles.inputsDropdownListChe,
                "quill-ui-next-dropdown-container",
                className,
              ].join(" ")}
              style={dropdownContainerStyle}
              initial="hidden"
              animate={"visible"}
              exit="hidden"
              variants={dropdownVariants}
            >
              {renderDropdownContent()}
            </motion.div>
          </AnimatePresence>
        ) : (
          <ActionSheet
            showHeader={false}
            {...actionsheet}
            show={isOpen}
            showSearchBar={false}
            content={renderDropdownContent()}
            type="bottom"
            onClose={actionsheet?.onClose}
            buttonPrimary={{
              label: "Done",
              onClick: () => {
                actionsheet?.onClose?.();
              },
            }}
          />
        )}
      </>
    )
  );
};

export default WheelpickerContainerAtom;
